import { Plot } from "../models/Plot";
import { PlotField } from "../models/PlotField";
import { PlotSchema } from "../models/PlotSchema";
import { PlotSchemaUiFieldGroup } from "../models/PlotSchemaUi";
import { PlotVisibilityFieldTypeValues } from "../models/PlotFieldTypeArgs";
import { PlottingService } from "./PlottingService";
import { PlottingTypeGuardService } from "./PlottingTypeGuardService";

export class PlotsUiService {

    public static getPlotSchemaUiFieldGroups = (plotSchema: PlotSchema): Array<PlotSchemaUiFieldGroup> | null => {

        if (!PlottingTypeGuardService.hasDefinedPlotSchemaUi(plotSchema)) return null;
        return plotSchema.ui?.field_groups as Array<PlotSchemaUiFieldGroup>;

    }

    public static getValueByPath = (configuration: any, path?: string | string[]): any => {
        if (configuration == null) return undefined;
        if (path === undefined || path === null) return undefined;

        const parts: string[] = Array.isArray(path)
            ? path
            : (String(path).match(/[^.[\]]+/g) || []);

        if (parts.length === 0) return configuration;

        let cur: any = configuration;
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (cur == null) return undefined;

            if (part === '*') {
                if (!Array.isArray(cur)) return undefined;
                const remainingParts = parts.slice(i + 1);
                return cur.map((item: any) => PlotsUiService.getValueByPath(item, remainingParts));
            }

            if (/^\d+$/.test(part)) {
                cur = cur[Number(part)];
            } else {
                cur = cur[part];
            }
        }
        return cur;
    }

    public static isFieldVisibleFromRoot = (field: PlotField, rootConfiguration?: Plot | null, _currentPath?: string[]) => {

        if (field.visible === false) return false;
        if (rootConfiguration != null) {
            const flattenedRootConfiguration = PlottingService.getFlattenedPlot(rootConfiguration, null);

            const visibleConfigs = field?.field_type_args?.visible_configs;
            if (Array.isArray(visibleConfigs)) {

                let visible = false;
                visibleConfigs.forEach((config: { visible_prop_path: string, visible_prop_value: any, visible_value?: PlotVisibilityFieldTypeValues, visible_parent?: string }) => {
                    const propPath = config?.visible_prop_path;
                    const expected = config?.visible_prop_value;
                    const expectedValueType = config?.visible_value;
                    const visibleParent = config?.visible_parent;

                    let targetPathParts: string[] = [];

                    if (visibleParent && Array.isArray(_currentPath)) {
                        let foundIndex = -1;
                        for (let i = _currentPath.length - 1; i >= 0; i--) {
                            if (_currentPath[i] === visibleParent && i + 1 < _currentPath.length && /^\d+$/.test(String(_currentPath[i + 1]))) {
                                foundIndex = i;
                                break;
                            }
                        }
                        if (foundIndex !== -1) {
                            const base = _currentPath.slice(0, foundIndex + 2);
                            const propParts = propPath.includes('.') ? propPath.split('.') : [propPath];
                            targetPathParts = [...base, ...propParts];
                        }
                    }

                    if (targetPathParts.length === 0) {
                        targetPathParts = propPath.includes('.')
                            ? propPath.split('.')
                            : (_currentPath ? [..._currentPath.slice(0, -1), propPath] : [propPath]);
                    }

                    const val = PlotsUiService.getValueByPath(flattenedRootConfiguration, targetPathParts);
                    const flatVal = Array.isArray(val) ? val.flat(Infinity) : val;
                    const matches = Array.isArray(flatVal)
                        ? flatVal.some((v: any) => PlotsUiService.valueMatchesPlotVisibilityFieldTypeValue(v, expectedValueType, expected) || v === expected)
                        : PlotsUiService.valueMatchesPlotVisibilityFieldTypeValue(flatVal, expectedValueType, expected) || flatVal === expected;
                    visible = visible || matches;
                })
                return visible;
            }
        }
        return true;
    }

    private static valueMatchesPlotVisibilityFieldTypeValue(value: any, visibilityValue?: PlotVisibilityFieldTypeValues, expected?: any): boolean {

        switch (visibilityValue) {
            case PlotVisibilityFieldTypeValues.Defined:
                return value !== undefined;
            case PlotVisibilityFieldTypeValues.Falsey:
                return !value;
            case PlotVisibilityFieldTypeValues.Truthy:
                return !!value;
            case PlotVisibilityFieldTypeValues.Contains:
                if (Array.isArray(expected) || typeof expected === 'string') {
                    return expected.includes(value);
                }
                console.warn(`PlotsUiService: visible_value 'contains' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
                return false;
            case PlotVisibilityFieldTypeValues.DoesNotContain:
                if (Array.isArray(expected) || typeof expected === 'string') {
                    return !expected.includes(value);
                }
                console.warn(`PlotsUiService: visible_value 'does_not_contain' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
                return false;
            default:
                return false;
        }

    }

}
